import type { ThemeKey, DefaultThemeStyles } from 'Src/types/theme';
import type { ReactNode } from 'react';
import React, { Children } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { ICONS } from 'Components/utils/icon/icons';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Text } from 'Components/frameworks/text/Text';
import { themes as textThemes } from 'Components/frameworks/text/Text.themes';
import { themes } from './Notification.themes';
type Props = {
  children?: ReactNode;
  className?: string;
  closeLabel?: string;
  hasClose?: boolean;
  iconName?: IconName;
  iconWidth?: string;
  id?: string;
  onClose?: () => unknown;
  text?: string;
  theme?: ThemeKey<typeof themes>;
  title?: string;
};
export const Notification = ({
  children,
  className,
  closeLabel,
  hasClose,
  iconName = ICONS.LIGHTBULB,
  iconWidth = '1.875rem',
  id,
  onClose,
  text,
  theme = 'default',
  title,
  ...rest
}: Props) => {
  if (!title && !text && !Children.count(children)) {
    return null;
  }

  const {
    thCloseButton,
    thCloseButtonLabel,
    thIcon,
    thInner,
    thMessage,
    thRoot,
    thSlot,
    thText,
    thTextTheme,
    thTitle,
  } = themes[theme] as DefaultThemeStyles<typeof themes>;
  return (
    <div {...rest} className={classnames(thRoot, className)} id={id}>
      {!!iconName && (
        <div className={classnames('leading-0', thIcon)}>
          <Icon name={iconName} width={iconWidth} />
        </div>
      )}
      <div className={thInner}>
        {(text || title) && (
          <div className={thMessage}>
            {!!title && !text && <p className={classnames('m-0', thTitle)}>{title}</p>}
            {!!title && !!text && <h3 className={classnames(thTitle)}>{title}</h3>}
            <Text
              className={thText}
              text={text}
              theme={thTextTheme as ThemeKey<typeof textThemes>}
            />
          </div>
        )}
        {Children.count(children) > 0 && <div className={thSlot}>{children}</div>}
      </div>
      {hasClose && onClose && (
        <button className={classnames('leading-0', thCloseButton)} onClick={onClose} type="button">
          {!!closeLabel && <span className={thCloseButtonLabel}>{closeLabel}</span>}
          <Icon name={ICONS.CROSS} width="0.625rem" />
        </button>
      )}
    </div>
  );
};
